/**
 * EntrepriseCharts — portail « Entreprise » (Batch #14.315).
 * Trois graphiques mensuels tirés de la tenue de livres (grand livre reporté) :
 * Dépenses · Revenus · Global (revenus vs dépenses + résultat cumulé).
 */
import { Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fmtCAD } from "./_shared";

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const kFmt = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1).replace(".0", "")} k$` : `${Math.round(v)} $`);

function ChartCard({ title, subtitle, testId, children }) {
  return (
    <div className="bg-white border rounded-2xl p-4 sm:p-5" data-testid={testId}>
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      {subtitle && <p className="text-[11px] text-gray-500 mb-2">{subtitle}</p>}
      <div className="h-44 sm:h-52 mt-2">{children}</div>
    </div>
  );
}

const tip = { formatter: (v, name) => [fmtCAD(v), name], contentStyle: { fontSize: 12, borderRadius: 8 } };

export default function EntrepriseCharts({ books, sections }) {
  let cum = 0;
  const rows = (books?.monthly || []).map((m) => {
    cum = Math.round((cum + m.revenues - m.expenses) * 100) / 100;
    return { label: MONTHS[m.m - 1], Revenus: m.revenues, Dépenses: m.expenses, Résultat: cum };
  });
  const empty = !rows.some((r) => r.Revenus || r.Dépenses);
  if (empty) {
    return (
      <div className="bg-white border border-dashed rounded-2xl p-5 text-center text-sm text-gray-500" data-testid="ent-portal-charts-empty">
        Vos graphiques apparaîtront ici dès que votre comptable aura saisi vos ventes et vos achats dans la tenue de livres.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="ent-portal-charts">
      {sections.includes("expenses") && (
        <ChartCard title="Dépenses par mois" subtitle="Achats et frais comptabilisés" testId="ent-portal-chart-expenses">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={6} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={kFmt} />
              <Tooltip {...tip} />
              <Bar dataKey="Dépenses" fill="#e11d48" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
      <ChartCard title="Revenus par mois" subtitle="Ventes comptabilisées (avant taxes)" testId="ent-portal-chart-revenues">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={6} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={kFmt} />
            <Tooltip {...tip} />
            <Bar dataKey="Revenus" fill="#059669" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard title="Vue globale" subtitle="Revenus vs dépenses et résultat cumulé" testId="ent-portal-chart-global">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={6} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={kFmt} />
            <Tooltip {...tip} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Revenus" fill="#059669" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Dépenses" fill="#e11d48" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="Résultat" stroke="#1e3a8a" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
